// The tenant page frame: /tenants/:id/<tab> (each tab is a nested route).
//
// Loads the tenant once (GET /platform/leagues/:id), renders the header + status strip + tabs, and hands
// the tenant to the active tab via outlet context. A write that returns
// `{ tenant }` calls apply(tenant), so every tab shows the server's state at
// once; writes that don't return it call reload().

import { Outlet, useParams } from 'react-router-dom'
import { TAB_LABELS, applyTenant, reloadTenant, tenantKey, type TenantContext } from './tenantContext'
import { ImpersonateButton } from './ImpersonateButton'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import { TENANT_TABS } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  AccountStatusPill,
  ButtonLink,
  Card,
  EmptyState,
  ErrorState,
  ExternalLinkIcon,
  InlineAlert,
  LivePill,
  LoadingState,
  PlanPill,
  SourcePill,
  RenewalPill,
  Tabs,
  buttonClasses,
  type TabItem,
} from '@/components/ui'
import { useQuery } from '@/lib/query'
import { useDocumentTitle } from '@/lib/hooks'
import { titleCase } from '@/lib/format'
import { tenantLinks } from '@/lib/tenant'

/** Signup only on a tenant that came through the signup on scorelm.com. */
const tabItems = (hasSignup: boolean): TabItem[] =>
  TENANT_TABS.filter((t) => t !== 'signup' || hasSignup).map((t) => ({ to: t, label: TAB_LABELS[t], tone: t === 'danger-zone' ? 'danger' : 'default' }))

export default function TenantLayout() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0

  const { data: tenant, error, status, refetch } = useQuery(validId ? tenantKey(id) : null, (signal) => api.tenants.get(id, signal))
  useDocumentTitle(tenant ? tenant.name : 'Tenant')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState
          title="Tenant not found"
          description={validId ? `There's no tenant with id ${id}. It may have been deleted.` : `“${rawId}” isn't a tenant id.`}
          action={<ButtonLink to="/tenants">Back to tenants</ButtonLink>}
        />
      </Card>
    )
  }
  if (status === 'loading' && !tenant) return <LoadingState label="Loading tenant…" />
  if (!tenant) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this tenant" onRetry={refetch} />
      </Card>
    )
  }

  const links = tenantLinks(tenant)
  const hosts = [tenant.domains.subdomain_host, tenant.custom_domain].filter(Boolean)
  const ctx: TenantContext = { tenant, reload: reloadTenant, apply: applyTenant }

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Tenants', to: '/tenants' }, { label: tenant.name }]}
        title={tenant.name}
        subtitle={[...hosts, titleCase(tenant.default_sport_id)].join(' · ')}
        badges={
          <>
            <AccountStatusPill account={tenant.account} />
            {tenant.account.status !== 'suspended' ? <LivePill isLive={tenant.account.is_live} /> : null}
            <PlanPill account={tenant.account} />
            <RenewalPill account={tenant.account} />
            <SourcePill source={tenant.source} signup={tenant.signup} />
          </>
        }
        actions={
          <>
            {links.publicUrl ? (
              <a href={links.publicUrl} target="_blank" rel="noreferrer" className={buttonClasses({ size: 'sm' })}>
                <ExternalLinkIcon size={14} />
                View site
              </a>
            ) : null}
            {/* A one-off email to one of its admins: the Email tab's compose. */}
            <ButtonLink size="sm" to={`/tenants/${tenant.id}/email`}>
              Email
            </ButtonLink>
            <ImpersonateButton tenantId={tenant.id} tenantName={tenant.name} />
          </>
        }
      />
      {tenant.account.status === 'suspended' && tenant.account.suspension ? (
        <InlineAlert tone="bad" title="Suspended: the public site, Fan AI and the league admin are locked">
          {tenant.account.suspension.reason}
        </InlineAlert>
      ) : null}
      <Tabs label="Tenant sections" items={tabItems(!!tenant.signup_id)} />
      <Outlet context={ctx} />
    </div>
  )
}
